<!-- eslint-disable prettier/prettier -->
<template>
  <div>
    <PageHeader title="การตรวจสอบภาพใบหน้าขณะเรียน" />
    <b-container fluid="xl">
      <div class="py-5">

        <!-- ตารางประวัติการดำเนินงานโดยผู้ดูแลระบบ -->
        <b-row class="mb-2" align-v="end">
          <b-col md="4">
            <label class="d-block font-weight-bold" style="font-size: 14px;">
              การดำเนินการโดยผู้ดูแลระบบ
            </label>
            <b-form-select
              v-model="historyFilter"
              :options="historyFilterOptions"
              @input="onHistoryFilterChange"
            ></b-form-select>
          </b-col>
        </b-row>

        <b-table
          :fields="historyFields"
          :items="memberActions"
          id="learning-face-history"
          :busy="isHistoryLoading"
          responsive
          sort-icon-left
          striped
          table-class="mt-3 border-bottom"
          show-empty
        >
          <template #empty>
            <div class="text-center py-3 text-muted">ไม่พบข้อมูล</div>
          </template>

          <template #table-busy>
            <div class="text-center text-danger my-4">
              <Loading />
            </div>
          </template>

          <template #cell(lfma_createdAt)="props">
            <span class="d-block">
              {{ formatThaiDate(props.item.lfma_createdAt).date }}
            </span>
            <span class="d-block">
              {{ formatThaiDate(props.item.lfma_createdAt).time }}
            </span>
          </template>

          <template #cell(action_label)="props">
            {{ props.item.action_label }}
          </template>

          <template #cell(lfma_adminName)="props">
            {{ props.item.lfma_adminName || "-" }}
          </template>

          <template #cell(lfma_actionDescriptionForStudent)="props">
            {{ props.item.lfma_actionDescriptionForStudent || "-" }}
          </template>

          <template #cell(lfma_actionDescriptionForStaff)="props">
            {{ props.item.lfma_actionDescriptionForStaff || "-" }}
          </template>
        </b-table>

        <div class="row mt-3">
          <div class="col-lg-6">
            <form class="form-inline">
              <div class="form-group mb-2">
                <label>แสดง</label>
              </div>
              <div class="form-group ml-2 mb-2">
                <b-form-select
                  v-model="historyPerPage"
                  :options="[10, 15, 30, 50]"
                  size="sm"
                  @input="setHistoryPerPage"
                ></b-form-select>
              </div>
              <div class="form-group ml-2 mb-2">
                <label>
                  จาก
                  {{ Number(historyTotal).toLocaleString() }}
                  รายการ
                </label>
              </div>
            </form>
          </div>
          <div class="col-lg-6">
            <nav class="float-right" aria-label="Page navigation example">
              <b-pagination
                v-model="historyCurrentPage"
                :total-rows="historyTotal"
                :per-page="historyPerPage"
                @change="setHistoryPage"
                aria-controls="learning-face-history"
              ></b-pagination>
            </nav>
          </div>
        </div>

        <!-- รูปภาพระหว่างเรียน -->
        <h6 class="font-weight-bold mt-5 mb-0">รูปภาพตอนเรียน</h6>

        <div class="mt-3 p-4 border rounded">
          <div v-if="isLoading" class="text-center text-danger my-4">
            <Loading />
          </div>

          <div v-else-if="!captures.length" class="text-center py-3 text-muted">
            ไม่พบข้อมูล
          </div>

          <b-row v-else>
            <b-col
              v-for="capture in captures"
              :key="capture.llfm_id"
              cols="6"
              md="4"
              lg="3"
              class="mb-4 custom-col-xl-5"
            >
              <!-- Image frame -->
              <div
                class="capture-frame capture-frame--clickable"
                role="button"
                tabindex="0"
                @click="openUserLearningFaceEditModal(capture)"
                @keyup.enter="openUserLearningFaceEditModal(capture)"
              >
                <img
                  v-if="!failedImages[capture.llfm_id]"
                  :src="imageUrl(capture)"
                  :style="gridImageStyle(capture)"
                  class="capture-image"
                  @error="onImageError(capture)"
                />
                <div v-else class="capture-missing text-muted">
                  ไม่พบรูปภาพ
                </div>
              </div>

              <div class="text-center pt-2" style="font-size: 13px;">
                {{ formatThaiDate(capture.llfm_createdAt).date }}
                {{ formatThaiDate(capture.llfm_createdAt).time }}
              </div>
            </b-col>
          </b-row>
        </div>

        <div class="row mt-3">
          <div class="col-lg-6">
            <form class="form-inline">
              <div class="form-group mb-2">
                <label>แสดง</label>
              </div>
              <div class="form-group ml-2 mb-2">
                <b-form-select
                  v-model="perPage"
                  :options="[10, 20, 30]"
                  size="sm"
                  @input="setFetchParamsPerPage"
                ></b-form-select>
              </div>
              <div class="form-group ml-2 mb-2">
                <label>
                  จาก
                  {{ Number(total).toLocaleString() }}
                  รายการ
                </label>
              </div>
            </form>
          </div>
          <div class="col-lg-6">
            <nav class="float-right" aria-label="Page navigation">
              <b-pagination
                v-model="currentPage"
                :total-rows="total"
                :per-page="perPage"
                @change="setFetchParamsPage"
                aria-controls="learning-face-list"
              ></b-pagination>
            </nav>
          </div>
        </div>

        <!-- การดำเนินการกับผู้เรียนคนนี้ -->
        <b-row class="mt-2 mb-4">
          <b-col
            v-for="option in actionOptions"
            :key="option.value"
            cols="6"
            md="3"
            lg="2"
            class="mb-2"
          >
            <b-button
              block
              class="font-weight-normal d-flex align-items-center justify-content-center"
              :variant="option.variant"
              :style="option.style"
              :pressed="selectedAction === option.value"
              @click="selectAction(option.value)"
            >
              <b-icon :icon="option.icon" class="mr-2"></b-icon>
              <span class="mt-2" style="line-height: 1;">{{ option.label }}</span>
            </b-button>
          </b-col>
        </b-row>

        <div v-if="selectedAction">
          <div v-if="selectedAction !== 'PASS'" class="mb-3">
            <label class="d-block font-weight-bold" style="font-size: 14px;">
              ข้อความที่ส่งแจ้งนักเรียน
            </label>
            <b-form-select
              v-model="form.messageId"
              :options="messageOptions"
              :style="messageSelectStyle"
              @input="applyTemplate"
            ></b-form-select>
          </div>

          <div v-if="selectedAction !== 'PASS'" class="mb-3">
            <label class="d-block font-weight-bold" style="font-size: 14px;">
              รายละเอียด (เห็นเฉพาะนักเรียน) <span class="text-danger">*</span>
            </label>
            <b-form-textarea v-model="form.reasonStudent" rows="4"></b-form-textarea>
          </div>

          <div class="mb-3">
            <label class="d-block font-weight-bold" style="font-size: 14px;">
              รายละเอียด (เห็นเฉพาะเจ้าหน้าที่)
            </label>
            <b-form-textarea v-model="form.reasonStaff" rows="4"></b-form-textarea>
          </div>

          <b-row align-v="center" class="mt-4">
            <b-col>
              <b-button variant="link" class="pl-0" @click="goBackToMembers">
                &larr; ย้อนกลับ
              </b-button>
            </b-col>
            <b-col cols="auto">
              <b-button
                variant="primary"
                :disabled="isSaving || !canSubmitAction"
                @click="submitAction"
              >
                {{ isSaving ? "กำลังบันทึก..." : "บันทึก" }}
              </b-button>
            </b-col>
          </b-row>
        </div>
      </div>
    </b-container>

    <!-- คลิกรูปแล้วเทียบกับรูปยืนยันตัวตนที่ผู้เรียนลงทะเบียนไว้ -->
    <b-modal
      v-model="isCaptureModalVisible"
      size="lg"
      centered
      :title="userLearningFaceModalTitle"
      cancel-title="ยกเลิก"
      cancel-variant="danger"
      ok-title="บันทึก"
      ok-variant="primary"
      footer-class="justify-content-end"
      :ok-disabled="isRotationSaving"
      @ok.prevent="saveUserLearningFaceImageRotation"
      @cancel="closeUserLearningFaceEditModal"
    >
      <b-row>
        <b-col cols="6">
          <div class="capture-frame capture-frame--tall">
            <img
              v-if="idCardPhotoUrl && !isIdCardPhotoFailed"
              :src="idCardPhotoUrl"
              class="capture-image"
              @error="isIdCardPhotoFailed = true"
            />
            <div v-else class="capture-missing text-muted">
              ไม่พบรูปยืนยันตัวตน
            </div>
          </div>
          <div class="text-center pt-2" style="font-size: 13px;">
            วันที่ยืนยันตัวตน: {{ idCardVerifiedAtLabel }}
          </div>
        </b-col>

        <b-col cols="6">
          <div class="capture-frame capture-frame--tall">
            <!-- หมุนรูปให้ตรงด้าน กด 4 ครั้งกลับมาที่เดิม -->
            <b-button
              v-if="selectedUserLearningFaceImage"
              class="capture-rotate"
              variant="light"
              title="หมุนรูป 90 องศา"
              @click="rotateSelectedUserLearningFaceImage"
            >
              <b-icon icon="arrow-clockwise"></b-icon>
            </b-button>
            <img
              v-if="selectedUserLearningFaceImage"
              :src="imageUrl(selectedUserLearningFaceImage)"
              :style="modalImageStyle"
              class="capture-image"
            />
          </div>
          <div class="text-center pt-2" style="font-size: 13px;">
            {{ selectedCaptureLabel }}
          </div>
        </b-col>
      </b-row>
    </b-modal>
  </div>
</template>

<script>
import PageHeader from "@/components/layouts/PageHeader";
import Loading from "@/components/shared/Loading";
import api from "@/services/api.js";
import AuthMixin from "@/mixins/auth-mixin.js";
import { checkSubMenuAccess } from "@/services/accessControl";

const FILE_ACCESS_SERVICE_URL = process.env.VUE_APP_FILE_ACCESS_SERVICE_URL;

const FRAME_RATIO = { grid: 0.75, modal: 1.2 };

const MESSAGE_TEMPLATES = {
  // ทัก
  NOTIFY: [
    "ตื่นลูก!! ลุกไปล้างหน้าล้างตาแล้วค่อยกลับมาเรียน อย่านอนเรียนนะลูก",
    "เก็บมือถือก่อนลูก!! ตอนนี้ตั้งใจเรียนก่อน กลั้นใจ อย่าเพิ่งเล่นมือถือระหว่างเรียนนะลูก",
    "หายไปไหนลูก!! ระหว่างเรียนมีระบบคอยดูแลนักเรียนที่ไม่ตั้งใจ ทุกคนตั้งกล้องให้เห็นหน้านะลูก",
    "ยังอยู่รึเปล่าลูก!! ถ้าลุกไปจากหน้าจอคอมฯ นานๆ ให้ logout ออกจากระบบก่อนนะลูก"
  ],
  // ดีด
  KICK: [
    "ระบบตรวจสอบไม่พบใบหน้าที่ชัดเจนเป็นระยะเวลานาน กรุณาปรับกล้องให้เห็นเต็มใบหน้า และไม่นำวัสดุใดๆ มาปิดบังกล้องหรือบังใบหน้า",
    "ระบบตรวจสอบไม่พบใบหน้าที่ชัดเจนเป็นระยะเวลานาน กรุณาเปิดไฟให้มีแสงสว่างเพียงพอ",
    "ระบบตรวจสอบพบโปรแกรม บันทึกภาพหน้าจอและโปรแกรมแต่งรูป ขอความร่วมมือให้นักเรียนเอาโปรแกรมเหล่านั้นแแกก่อนเข้าเรียน เพื่อให้การเรียนเป็นไปได้อย่างมีประสิทธิภาพและไม่ขัดข้อง"
  ],
  // ล็อค
  LOCK: [
    "ระบบตรวจสอบไม่พบใบหน้าที่ชัดเจนเป็นระยะเวลานาน กรุณาปรับกล้องให้เห็นเต็มใบหน้า และไม่นำวัสดุใดๆ มาปิดบังกล้องหรือบังใบหน้า : ระบบแจ้งเตือนการใช้งาน 5 ครั้ง นักเรียนถูกแจ้งเตือนครั้งที่ [1] - หากแจ้งเตือนครบ 5 ครั้ง ระบบจะทำการล็อคการใช้งานชั่วคราว"
  ]
};

export default {
  name: "LearningFaceMonitoringPage",
  mixins: [AuthMixin],
  components: { PageHeader, Loading },
  data() {
    return {
      isLoading: false,
      isSaving: false,
      perPage: 10,
      currentPage: 1,
      total: 0,
      captures: [],
      failedImages: {},
      idCardPhotoPath: null,
      idCardVerifiedAt: null,
      isIdCardPhotoFailed: false,
      selectedUserLearningFaceImage: null,
      userLearningFaceImageRotation: 0,
      isRotationSaving: false,
      isCaptureModalVisible: false,
      selectedAction: null,
      memberActions: [],
      isHistoryLoading: false,
      historyFilter: null,
      historyPerPage: 10,
      historyCurrentPage: 1,
      historyTotal: 0,
      historyFilterOptions: [
        { value: null, text: "ทั้งหมด" },
        { value: "NOTIFY", text: "ทัก" },
        { value: "KICK", text: "ดีด" },
        { value: "LOCK", text: "ล็อค" },
        { value: "PASS", text: "ตรวจสอบผ่าน" }
      ],
      historyFields: [
        {
          key: "action_label",
          label: "การดำเนินการ",
          thStyle: { width: "12%" }
        },
        {
          key: "lfma_createdAt",
          label: "วันที่",
          sortable: true,
          thStyle: { width: "13%" }
        },
        { key: "lfma_adminName", label: "ทำโดย", thStyle: { width: "15%" } },
        {
          key: "lfma_actionDescriptionForStudent",
          label: "เหตุผล-ให้นักเรียน",
          thStyle: { width: "27%" }
        },
        {
          key: "lfma_actionDescriptionForStaff",
          label: "เหตุผล-เจ้าหน้าที่",
          thStyle: { width: "33%" }
        }
      ],
      form: {
        messageId: null,
        reasonStudent: "",
        reasonStaff: ""
      },
      actionOptions: [
        {
          value: "NOTIFY",
          label: "ทัก",
          icon: "bell",
          variant: "warning",
          style: null
        },
        {
          value: "KICK",
          label: "ดีด",
          icon: "person-x",
          variant: null,
          style: {
            backgroundColor: "#f5a15e",
            borderColor: "#f5a15e",
            color: "#212529"
          }
        },
        {
          value: "LOCK",
          label: "ล็อค",
          icon: "lock",
          variant: "danger",
          style: null
        },
        {
          value: "PASS",
          label: "ตรวจสอบผ่าน",
          icon: "check-circle",
          variant: "success",
          style: null
        }
      ],
      actionColors: {
        NOTIFY: { background: "#FFC107", color: "#212529" },
        KICK: { background: "#F99F4D", color: "#212529" },
        LOCK: { background: "#DC3545", color: "#FFF" },
        PASS: { background: "#28A745", color: "#FFF" }
      }
    };
  },
  computed: {
    canAccessLearningFace() {
      return checkSubMenuAccess(
        { menu: "learning-face-monitoring" },
        this.getCurrentUser()
      );
    },

    memberId() {
      return Number(this.$route.params.mId);
    },

    // ทัก ดีด ล็อก ต้องกรอกข้อความแจ้งเตือน
    isReasonStudentRequired() {
      return Boolean(this.selectedAction) && this.selectedAction !== "PASS";
    },

    canSubmitAction() {
      if (!this.isReasonStudentRequired) return true;
      return (this.form.reasonStudent || "").trim().length > 0;
    },

    userLearningFaceModalTitle() {
      if (!this.selectedUserLearningFaceImage) return "รูปภาพ";
      // eslint-disable-next-line prettier/prettier
      const at = this.formatThaiDate(this.selectedUserLearningFaceImage.llfm_createdAt);
      return `รูปภาพ ${at.date} ${at.time}`;
    },

    selectedCaptureLabel() {
      if (!this.selectedUserLearningFaceImage) return "-";
      // eslint-disable-next-line prettier/prettier
      const at = this.formatThaiDate(this.selectedUserLearningFaceImage.llfm_createdAt);
      return `${at.date} ${at.time}`;
    },

    modalImageStyle() {
      return this.rotationStyle(
        this.userLearningFaceImageRotation,
        FRAME_RATIO.modal
      );
    },

    idCardPhotoUrl() {
      if (!this.idCardPhotoPath) return null;
      const token = localStorage.getItem("token");
      return `${FILE_ACCESS_SERVICE_URL}/${this.idCardPhotoPath}?token=${token}`;
    },

    idCardVerifiedAtLabel() {
      if (!this.idCardVerifiedAt) return "-";
      const at = this.formatThaiDate(this.idCardVerifiedAt);
      return `${at.date} ${at.time}`;
    },

    latestUserLearningFaceImage() {
      return this.captures.length ? this.captures[0] : null;
    },

    // Update dropdown background color based on an admin action
    messageSelectStyle() {
      const color = this.actionColors[this.selectedAction];
      if (!color) return {};
      return {
        backgroundColor: color.background,
        borderColor: color.background,
        color: color.color
      };
    },

    messageOptions() {
      const templates = MESSAGE_TEMPLATES[this.selectedAction] || [];
      return [
        { value: null, text: "กรุณาเลือกการแจ้งเตือนนักเรียน" },
        ...templates.map(text => ({ value: text, text }))
      ];
    },

    // Show confirmation modal for KICK and LOCK action
    actionWarning() {
      if (this.selectedAction === "KICK") {
        return "การบันทึกจะนำผู้เรียนออกจากระบบทันที และแจ้งเตือนไปยังแอปบนมือถือ";
      }
      if (this.selectedAction === "LOCK") {
        return "การบันทึกจะนำผู้เรียนออกจากระบบ และระงับไม่ให้เข้าสู่ระบบได้อีกจนกว่าผู้ดูแลจะปลดล็อค";
      }
      return null;
    },

    // ใช้ในหัวข้อและปุ่มของกล่องยืนยัน
    selectedActionLabel() {
      const option = this.actionOptions.find(
        option => option.value === this.selectedAction
      );
      return option ? option.label : "";
    }
  },
  mounted() {
    // Check if the comId has permission to this page
    if (!this.canAccessLearningFace) {
      const company = window.location.pathname.split("/")[1];
      window.location.replace(
        `${window.location.origin}/${company}/un-authorized`
      );
      return;
    }

    this.fetchUserLearningFaceImages();
    this.fetchAdminActionToMemberHistories();
    this.fetchMemberProfile();
  },
  methods: {
    async fetchMemberProfile() {
      try {
        const response = await api.get(`/v1/members/${this.memberId}`);
        const member = response.data.data || {};

        this.idCardPhotoPath = member.faceWithIdCardPhotoPath || null;
        this.idCardVerifiedAt =
          member.idCardVerifiedAt || member.registerDate || null;
      } catch (error) {
        console.error("Error fetching member profile:", error);
      }
    },

    openUserLearningFaceEditModal(userLearningFaceImage) {
      this.selectedUserLearningFaceImage = userLearningFaceImage;
      this.isIdCardPhotoFailed = false;
      this.userLearningFaceImageRotation =
        Number(userLearningFaceImage.llfm_faceRotation) || 0;
      this.isCaptureModalVisible = true;
    },

    rotateSelectedUserLearningFaceImage() {
      this.userLearningFaceImageRotation =
        (this.userLearningFaceImageRotation + 90) % 360;
    },

    rotationStyle(rotation, ratio) {
      const degree = (((Number(rotation) || 0) % 360) + 360) % 360;

      if (degree === 0) return {};
      if (degree === 180) return { transform: "rotate(180deg)" };

      return {
        width: `${ratio * 100}%`,
        height: `${100 / ratio}%`,
        top: "50%",
        left: "50%",
        transform: `translate(-50%, -50%) rotate(${degree}deg)`
      };
    },

    gridImageStyle(capture) {
      return this.rotationStyle(capture.llfm_faceRotation, FRAME_RATIO.grid);
    },

    async saveUserLearningFaceImageRotation() {
      if (!this.selectedUserLearningFaceImage) return;

      // If the same rotation degree, close the modal
      if (
        this.userLearningFaceImageRotation ===
        (Number(this.selectedUserLearningFaceImage.llfm_faceRotation) || 0)
      ) {
        this.closeUserLearningFaceEditModal();
        return;
      }

      this.isRotationSaving = true;
      try {
        await api.patch(
          `/v1/learning-face-images/${this.selectedUserLearningFaceImage.llfm_id}/rotation`,
          { rotation: this.userLearningFaceImageRotation },
          { headers: { ComId: this.$store.state.Company.companyInfo.id } }
        );

        // Update image rotation degree locally
        this.$set(
          this.selectedUserLearningFaceImage,
          "llfm_faceRotation",
          this.userLearningFaceImageRotation
        );

        this.$bvToast.toast("บันทึกการหมุนรูปเรียบร้อย", {
          title: "สำเร็จ",
          variant: "success",
          solid: true
        });
        this.closeUserLearningFaceEditModal();
      } catch (error) {
        console.error("Error saving capture rotation:", error);
        this.$bvToast.toast("บันทึกการหมุนรูปไม่สำเร็จ", {
          title: "ผิดพลาด",
          variant: "danger",
          solid: true
        });
      } finally {
        this.isRotationSaving = false;
      }
    },

    closeUserLearningFaceEditModal() {
      this.isCaptureModalVisible = false;
      this.selectedUserLearningFaceImage = null;
    },

    async fetchUserLearningFaceImages(page = this.currentPage) {
      this.isLoading = true;
      this.currentPage = page;

      try {
        const response = await api.get("/v1/learning-face-images", {
          params: {
            page: this.currentPage,
            per_page: this.perPage,
            m_id: this.memberId
          },
          headers: { ComId: this.$store.state.Company.companyInfo.id }
        });

        this.captures = response.data.data || [];
        this.total = (response.data.meta && response.data.meta.total) || 0;
        this.failedImages = {};
      } catch (error) {
        console.error("Error fetching learning face captures:", error);
        this.captures = [];
        this.total = 0;
      } finally {
        this.isLoading = false;
      }
    },

    imageUrl(capture) {
      const token = localStorage.getItem("token");
      return `${FILE_ACCESS_SERVICE_URL}/${capture.llfm_facePath}?token=${token}`;
    },

    onImageError(capture) {
      this.$set(this.failedImages, capture.llfm_id, true);
    },

    selectAction(action) {
      this.selectedAction = action;
      this.form.messageId = null;
    },

    applyTemplate(text) {
      if (text) {
        this.form.reasonStudent = text;
      }
    },

    async fetchAdminActionToMemberHistories(page = this.historyCurrentPage) {
      this.isHistoryLoading = true;
      this.historyCurrentPage = page;

      try {
        const response = await api.get(
          `/v1/learning-face-images/members/${this.memberId}/action-logs`,
          {
            params: {
              action: this.historyFilter,
              page: this.historyCurrentPage,
              per_page: this.historyPerPage
            },
            headers: { ComId: this.$store.state.Company.companyInfo.id }
          }
        );
        this.memberActions = response.data.data || [];
        this.historyTotal =
          (response.data.meta && response.data.meta.total) || 0;
      } catch (error) {
        console.error("Error fetching member actions:", error);
        this.memberActions = [];
        this.historyTotal = 0;
      } finally {
        this.isHistoryLoading = false;
      }
    },

    setHistoryPage(page) {
      this.fetchAdminActionToMemberHistories(page);
    },

    setHistoryPerPage() {
      this.fetchAdminActionToMemberHistories(1);
    },

    // เปลี่ยนตัวกรองแล้วต้องกลับหน้าแรก ไม่งั้นอาจค้างอยู่หน้าที่ไม่มีข้อมูล
    onHistoryFilterChange() {
      this.fetchAdminActionToMemberHistories(1);
    },

    async submitAction() {
      if (!this.selectedAction) return;

      if (this.actionWarning) {
        const confirmed = await this.$bvModal.msgBoxConfirm(
          this.actionWarning,
          {
            title: `ยืนยันการ${this.selectedActionLabel}`,
            size: "md",
            buttonSize: "sm",
            okVariant: "danger",
            okTitle: `ยืนยันการ${this.selectedActionLabel}`,
            cancelTitle: "ยกเลิก",
            cancelVariant: "light",
            footerClass: "p-2",
            hideHeaderClose: false,
            centered: true
          }
        );

        if (!confirmed) return;
      }

      this.isSaving = true;
      try {
        const response = await api.post("/v1/learning-face-images/actions", {
          m_id: this.memberId,
          action: this.selectedAction,
          cer_id: this.latestUserLearningFaceImage
            ? this.latestUserLearningFaceImage.cer_id
            : null,
          lec_id: this.latestUserLearningFaceImage
            ? this.latestUserLearningFaceImage.lec_id
            : null,
          reason_student: this.form.reasonStudent,
          reason_staff: this.form.reasonStaff
        });

        const saved = response.data.data;
        if (saved && saved.lfma_pushStatus === "FAILED") {
          this.$bvToast.toast(
            "บันทึกการดำเนินการแล้ว แต่ส่งแจ้งเตือนไปยังแอปไม่สำเร็จ",
            { title: "แจ้งเตือนไม่สำเร็จ", variant: "warning", solid: true }
          );
        } else {
          this.$bvToast.toast(response.data.message, {
            title: "สำเร็จ",
            variant: "success",
            solid: true
          });
        }

        this.resetForm();
        // รายการใหม่อยู่บนสุด เลยต้องกลับไปหน้าแรกถึงจะเห็น
        this.fetchAdminActionToMemberHistories(1);
      } catch (error) {
        console.error("Error submitting learning face action:", error);
        const message =
          (error.response &&
            error.response.data &&
            error.response.data.message) ||
          "บันทึกไม่สำเร็จ กรุณาลองใหม่อีกครั้ง";
        this.$bvToast.toast(message, {
          title: "เกิดข้อผิดพลาด",
          variant: "danger",
          solid: true
        });
      } finally {
        this.isSaving = false;
      }
    },

    resetForm() {
      this.selectedAction = null;
      this.form.messageId = null;
      this.form.reasonStudent = "";
      this.form.reasonStaff = "";
    },

    goBackToMembers() {
      this.$router.push({ name: "member.index" });
    },

    setFetchParamsPage(page) {
      this.fetchUserLearningFaceImages(page);
    },

    setFetchParamsPerPage() {
      this.fetchUserLearningFaceImages(1);
    },

    formatThaiDate(dateString) {
      if (!dateString || dateString === "-") return { date: "-", time: "" };
      const date = new Date(dateString);
      const datePart = date.toLocaleDateString("th-TH", {
        year: "numeric",
        month: "short",
        day: "numeric"
      });
      const timePart = date.toLocaleTimeString("th-TH", {
        hour: "2-digit",
        minute: "2-digit"
      });
      return { date: datePart, time: `${timePart} น.` };
    }
  }
};
</script>

<style scoped>
::v-deep #learning-face-history thead th {
  text-align: center;
  vertical-align: middle;
  white-space: nowrap;
}

.capture-frame {
  position: relative;
  background-color: #f5f6f7;
  overflow: hidden;
  padding-top: 75%;
}

.capture-frame--clickable {
  cursor: pointer;
}

.capture-frame--clickable:hover,
.capture-frame--clickable:focus {
  outline: 2px solid #007bff;
  outline-offset: 2px;
}

.capture-frame--tall {
  padding-top: 120%;
}

.capture-frame .capture-rotate {
  position: absolute;
  top: 8px;
  right: 8px;
  z-index: 2;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  padding: 0;
  border-radius: 50%;
  background-color: #fff;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.2);
}

.capture-image {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  object-fit: contain;
}

.capture-missing {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 13px;
}

::v-deep .custom-select {
  height: calc(1.5em + 0.95rem + 2px) !important;
  padding-top: 0.375rem;
  padding-bottom: 0.375rem;
  line-height: 1.5;
}

@media (min-width: 1200px) {
  .custom-col-xl-5 {
    flex: 0 0 20%;
    max-width: 20%;
  }
}
</style>
